/* Proprietary - see LICENSE */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Login.css';
import apiClient from '../config/apiClient';

const MfaSetup = ({ mfaToken, onComplete, onCancel }) => {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showManualKey, setShowManualKey] = useState(false);

  useEffect(() => {
    const fetchSetup = async () => {
      try {
        const response = await apiClient.post('/auth/mfa/setup', {}, {
          headers: { 'x-mfa-token': mfaToken },
        });
        setQrCode(response.data.qrCode);
        setManualKey(response.data.manualKey);
      } catch (err) {
        setError(err.response?.data?.message || t('mfa.setupError'));
      } finally {
        setSetupLoading(false);
      }
    };
    fetchSetup();
  }, [mfaToken, t]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/mfa/enable', { code }, {
        headers: { 'x-mfa-token': mfaToken },
      });

      // Save backup codes to show user
      if (response.data.backupCodes) {
        setBackupCodes(response.data.backupCodes);
      }

      // Store tokens
      sessionStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        sessionStorage.setItem('refreshToken', response.data.refresh_token);
      }
      sessionStorage.setItem('user', JSON.stringify(response.data.user));

      const role = response.data?.user?.role;
      const isMultiTenant = response.data?.user?.multiTenant === true;
      const shouldSelectTenant = ['super_admin', 'owner', 'provider_admin'].includes(role) || isMultiTenant;
      if (shouldSelectTenant) {
        sessionStorage.removeItem('selectedTenantId');
      } else if (response.data?.user?.tenantId) {
        sessionStorage.setItem('selectedTenantId', response.data.user.tenantId);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('mfa.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    onComplete();
  };

  // Show backup codes after successful setup
  if (backupCodes) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h2 style={{ margin: '0 0 0.5rem', color: '#0f172a' }}>{t('mfa.backupCodesTitle')}</h2>
            <p style={{ fontSize: '0.9rem', color: '#64748b' }}>{t('mfa.backupCodesSubtitle')}</p>
          </div>

          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            fontFamily: 'monospace',
            fontSize: '1rem',
            lineHeight: '2',
            textAlign: 'center',
          }}>
            {backupCodes.map((code, i) => (
              <div key={i}>{code}</div>
            ))}
          </div>

          <p style={{ fontSize: '0.85rem', color: '#ef4444', marginBottom: '1.5rem', textAlign: 'center' }}>
            {t('mfa.backupCodesWarning')}
          </p>

          <button
            className="btn btn-primary btn-block"
            onClick={handleContinue}
          >
            {t('mfa.continue')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h2 style={{ margin: '0 0 0.5rem', color: '#0f172a' }}>{t('mfa.setupTitle')}</h2>
          <p style={{ fontSize: '0.9rem', color: '#64748b' }}>{t('mfa.setupSubtitle')}</p>
        </div>

        {setupLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner" style={{
              width: 40, height: 40,
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto',
            }} />
          </div>
        ) : (
          <>
            {/* QR Code */}
            {qrCode && (
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                  {t('mfa.scanQrCode')}
                </p>
                <img
                  src={qrCode}
                  alt="MFA QR Code"
                  style={{ width: 200, height: 200, margin: '0 auto', display: 'block' }}
                />
              </div>
            )}

            {/* Manual key toggle */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <button
                type="button"
                onClick={() => setShowManualKey(!showManualKey)}
                style={{
                  background: 'none', border: 'none', color: '#4f46e5',
                  cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline',
                }}
              >
                {showManualKey ? t('mfa.hideManualKey') : t('mfa.showManualKey')}
              </button>
              {showManualKey && (
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  marginTop: '0.5rem',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                  userSelect: 'all',
                }}>
                  {manualKey}
                </div>
              )}
            </div>

            {/* Verification form */}
            <form onSubmit={handleVerify} className="login-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="mfa-code">{t('mfa.enterCode')}</label>
                <input
                  type="text"
                  id="mfa-code"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(val);
                  }}
                  required
                  placeholder="000000"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading || code.length !== 6}>
                {loading ? t('mfa.verifying') : t('mfa.verifyAndEnable')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MfaSetup;
